import { z } from 'zod'
import type { AnyFieldApi, ReactFormExtendedApi } from '@tanstack/react-form'
import { Select, type SelectOption } from '../../Select'
import { Label } from '../../Label'
import { Alert } from '../../Alert'
import { Flex } from '@radix-ui/themes'

/**
 * Catch-all TanStack Form React API type. See FormField for rationale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyReactFormApi = ReactFormExtendedApi<any, any, any, any, any, any, any, any, any, any, any, any>

interface FormSelectFieldProps {
  name: string
  label?: string
  options: SelectOption[]
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  validationSchema?: z.ZodSchema<unknown> | (() => z.ZodSchema<unknown>)
  form: AnyReactFormApi
}

export const FormSelectField = ({
  name,
  label,
  options,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  validationSchema,
  form,
}: FormSelectFieldProps) => {
  const validators = validationSchema ? {
    onBlur: ({ value }: { value: unknown }) => {
      const schema = typeof validationSchema === 'function' ? validationSchema() : validationSchema
      const result = schema.safeParse(value)
      if (!result.success) {
        return result.error.issues[0]?.message
      }
      return undefined
    }
  } : {}

  return (
    <form.Field
      name={name}
      validators={validators}
    >
      {(field: AnyFieldApi) => (
        <Flex direction="column" gap="2" className="w-full">
          {label && (
            <Label htmlFor={field.name}>
              {label}
              {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
          )}

          <Select
            id={field.name}
            name={field.name}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            value={field.state.value as string}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
          />

          {field.state.meta.errors.length > 0 && (
            <Alert variant="error">
              {field.state.meta.errors.join(', ')}
            </Alert>
          )}
        </Flex>
      )}
    </form.Field>
  )
}
